import React from 'react';

interface PricingTierProps {
  name: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  description,
  features,
  isPopular,
}) => {
  return (
    <div
      className={`relative bg-white dark:bg-slate-800 rounded-2xl shadow-lg ${
        isPopular ? 'ring-2 ring-primary-main' : ''
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-primary-main text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}
      <div className="p-8">
        <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">{name}</h3>
        <p className="text-slate-700 dark:text-slate-300 mb-6">{description}</p>
        <div className="mb-6">
          <span className="text-4xl font-bold text-slate-900 dark:text-slate-50">{price}</span>
          {price !== 'Free' && <span className="text-slate-700 dark:text-slate-300">/month</span>}
        </div>
        <ul className="space-y-4 mb-8">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center">
              <svg
                className="w-5 h-5 text-primary-main mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-slate-700 dark:text-slate-300">{feature}</span>
            </li>
          ))}
        </ul>
        <button
          className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
            isPopular
              ? 'bg-primary-main hover:bg-primary-dark text-white'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-50 border-2 border-primary-main hover:bg-primary-main hover:text-white'
          }`}
        >
          Get Started
        </button>
      </div>
    </div>
  );
};

const PricingSection: React.FC = () => {
  const tiers = [
    {
      name: 'Bronze',
      price: 'Free',
      description: 'Perfect for getting started with crypto trading',
      features: [
        '30 days free',
        'All trading signals',
        // 'Daily market updates',
        // 'Community access',
       // 'Email support',
      ],
    },
    {
      name: 'Silver',
      price: '$99',
      description: 'For serious traders who want more information',
      features: [
        'Features of "Bronze"',
        'Real-time signals statistics',
        'Stop Loss hit status'
        // 'Automated Algorithmic Trading',
        // 'Advanced trading signals',
        // 'Real-time alerts',
        // 'Priority support',
        //'Trading strategy guides',
       // 'Performance analytics',
      ],
      isPopular: true,
    },
    {
      name: 'Gold',
      price: '$299',
      description: 'Automated Trading',
      features: [
        'Features of "Silver"',
        'Automated Trading',
        'Daily/Weekly Trading statistics',
      ],
    },
  ];

  return (
    <section className="py-16 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-slate-700 dark:text-slate-300 text-lg max-w-2xl mx-auto">
            Choose the plan that best fits your trading needs. All plans include access to our basic
            features.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <PricingTier key={index} {...tier} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
